import { Check, ChevronLeft, ChevronRight, Eye, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import type {
  ShiftProductionForm,
  ShiftProductionFormListItem,
} from "../../../shared/shift-production-form/contracts";
import { productionLines, shiftLabels } from "./line-options";
import { ShiftFormReadOnly } from "./ShiftFormReadOnly";
import { useManagerFormWorkspace } from "./useManagerFormWorkspace";

const statusText: Record<ShiftProductionFormListItem["status"], string> = {
  DRAFT: "Taslak",
  RETURNED: "Düzeltmede",
  PENDING_SHIFT_SUPERVISOR: "Vardiya amiri onayı",
  PENDING_PRODUCTION_ENGINEER: "Üretim şefi onayı",
  PENDING_PRODUCTION_MANAGER: "Üretim müdürü onayı",
  COMPLETED: "Tamamlandı",
};
const stageText = {
  SHIFT_SUPERVISOR: "Vardiya amiri",
  PRODUCTION_ENGINEER: "Üretim şefi",
  PRODUCTION_MANAGER: "Üretim müdürü",
} as const;

export function ManagerFormWorkspace({
  initialFormId,
}: {
  initialFormId: string | null;
}) {
  const workspace = useManagerFormWorkspace(initialFormId);
  if (workspace.phase === "error" && workspace.items.length === 0)
    return (
      <section className="shift-form-workspace">
        <div className="shift-form-empty">
          <h3>Form listesi açılamadı</h3>
          <p>{workspace.error}</p>
          <button
            type="button"
            className="shift-form-secondary"
            onClick={() => void workspace.reload()}
          >
            Tekrar dene
          </button>
        </div>
      </section>
    );
  return (
    <section className="shift-form-workspace" aria-labelledby="manager-heading">
      <div className="shift-form-workspace-heading">
        <div>
          <h2 id="manager-heading">Üretim formları</h2>
          <p>Sayaçlar ve kayıtlar yalnız erişiminiz olan hatları kapsar.</p>
        </div>
      </div>
      <nav className="shift-manager-tabs" aria-label="Form görünümleri">
        {(["pending", "accessible", "completed"] as const).map((view) => (
          <button
            type="button"
            key={view}
            className={workspace.view === view ? "is-current" : ""}
            onClick={() => workspace.changeView(view)}
          >
            {view === "pending"
              ? "Onayımı bekleyenler"
              : view === "accessible"
                ? "Erişebildiğim formlar"
                : "Tamamlananlar"}{" "}
            <span>{workspace.counts[view]}</span>
          </button>
        ))}
      </nav>
      <form
        className="shift-manager-filters"
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          workspace.changeFilters({
            lineName: String(data.get("lineName") ?? ""),
            productionDate: String(data.get("date") ?? ""),
            shift: String(data.get("shift") ?? ""),
            operatorName: String(data.get("operator") ?? ""),
            status: String(data.get("status") ?? ""),
          });
        }}
      >
        <label>
          <span>Hat</span>
          <select name="lineName">
            <option value="">Tümü</option>
            {productionLines.map((line) => (
              <option key={line}>{line}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Tarih</span>
          <input name="date" type="date" />
        </label>
        <label>
          <span>Vardiya</span>
          <select name="shift">
            <option value="">Tümü</option>
            {Object.entries(shiftLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Operatör</span>
          <input name="operator" />
        </label>
        <label>
          <span>Durum</span>
          <select name="status">
            <option value="">Tümü</option>
            {Object.entries(statusText).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="shift-form-secondary">
          Filtrele
        </button>
      </form>
      {workspace.error ? (
        <p className="shift-form-error" role="alert">
          {workspace.error}
        </p>
      ) : null}
      <div className="shift-manager-layout">
        <div className="shift-manager-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Form</th>
                <th>Hat</th>
                <th>Vardiya</th>
                <th>Operatör</th>
                <th>Durum</th>
                <th>
                  <span className="sr-only">İncele</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {workspace.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.formNo}</strong>
                    <small>{item.productionDate}</small>
                  </td>
                  <td>{item.lineName}</td>
                  <td>{item.shift ? shiftLabels[item.shift] : "Belirtilmedi"}</td>
                  <td>{item.operatorName}</td>
                  <td>
                    <span className={`shift-status is-${item.status}`}>
                      {statusText[item.status]}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      disabled={workspace.busy}
                      onClick={() => void workspace.open(item.id)}
                      aria-label={`${item.formNo} formunu incele`}
                    >
                      <Eye size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {workspace.items.length === 0 ? (
            <p className="shift-manager-no-results">Bu görünümde kayıt yok.</p>
          ) : null}
          <div className="shift-pagination">
            <button
              type="button"
              disabled={(workspace.filters.offset ?? 0) === 0}
              onClick={() =>
                workspace.setFilters((current) => ({
                  ...current,
                  offset: Math.max(0, (current.offset ?? 0) - 20),
                }))
              }
            >
              <ChevronLeft size={16} />
              Önceki
            </button>
            <span>{workspace.total} kayıt</span>
            <button
              type="button"
              disabled={(workspace.filters.offset ?? 0) + 20 >= workspace.total}
              onClick={() =>
                workspace.setFilters((current) => ({
                  ...current,
                  offset: (current.offset ?? 0) + 20,
                }))
              }
            >
              Sonraki
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        {workspace.selected ? (
          <FormDetail
            form={workspace.selected}
            busy={workspace.busy}
            onDecide={workspace.decide}
            onClose={() => workspace.setSelected(null)}
          />
        ) : (
          <aside className="shift-manager-detail is-empty">
            <Eye size={22} />
            <p>İncelemek için bir form seçin.</p>
          </aside>
        )}
      </div>
    </section>
  );
}

export function FormDetail({
  form,
  busy,
  onDecide,
  onClose,
  adminActions,
}: {
  form: ShiftProductionForm;
  busy: boolean;
  onDecide?: (
    action: "APPROVE",
    explanation: string,
    missing: string,
  ) => Promise<void>;
  onClose?: () => void;
  adminActions?: ReactNode;
}) {
  return (
    <aside
      className="shift-manager-detail"
      aria-label={`${form.formNo} form ayrıntısı`}
    >
      <div className="shift-detail-heading">
        <div>
          <span className={`shift-status is-${form.status}`}>
            {statusText[form.status]}
          </span>
          <h3>{form.formNo}</h3>
          <p>
            {form.payload.lineName ?? "Hat seçilmedi"} ·{" "}
            {form.payload.productionDate ?? "Tarih yok"}
          </p>
        </div>
        {onClose ? (
          <button type="button" onClick={onClose}>
            Kapat
          </button>
        ) : null}
      </div>
      {form.hasMissingMeasurements ? (
        <p className="shift-missing-note">Formda açıklanmış eksik ölçüm var.</p>
      ) : null}
      <dl>
        <div>
          <dt>Kayıt sürümü</dt>
          <dd>v{form.version}</dd>
        </div>
        <div>
          <dt>Düzeltme sürümü</dt>
          <dd>{form.revisionNumber}</dd>
        </div>
        <div>
          <dt>Onay aşaması</dt>
          <dd>
            {form.currentApprovalStage
              ? stageText[form.currentApprovalStage]
              : "Açık onay aşaması yok"}
          </dd>
        </div>
      </dl>
      {form.canDecide && onDecide ? (
        <DecisionForm form={form} busy={busy} onDecide={onDecide} />
      ) : null}
      {adminActions}
      <ShiftFormReadOnly payload={form.payload} />
      <h4>Gönderilmiş sürümler</h4>
      {form.revisions.length ? (
        form.revisions.map((revision) => (
          <details key={revision.revisionNumber}>
            <summary>
              Sürüm {revision.revisionNumber} ·{" "}
              {new Date(revision.submittedAt).toLocaleString("tr-TR")}
            </summary>
            <ShiftFormReadOnly payload={revision.snapshot} />
          </details>
        ))
      ) : (
        <p>Henüz gönderilmiş sürüm yok.</p>
      )}
      <h4>Onay kararları</h4>
      <ol className="shift-audit-list">
        {form.decisions.length ? (
          form.decisions.map((decision) => (
            <li key={decision.id}>
              <span>
                {decision.decision === "APPROVED" ? (
                  <Check size={15} />
                ) : (
                  <RotateCcw size={15} />
                )}
              </span>
              <div>
                <strong>{decision.actorName}</strong>
                <p>
                  {stageText[decision.stage]} ·{" "}
                  {decision.decision === "APPROVED"
                    ? "Onaylandı"
                    : "Düzeltmeye gönderildi"}{" "}
                  · {new Date(decision.decidedAt).toLocaleString("tr-TR")}
                </p>
                <p>
                  {decision.explanation ||
                    decision.missingMeasurementAcceptanceReason ||
                    "Açıklama yok"}
                </p>
              </div>
            </li>
          ))
        ) : (
          <li>Henüz onay kararı yok.</li>
        )}
      </ol>
      <h4>Denetim geçmişi</h4>
      <ol className="shift-audit-list">
        {form.history.map((event) => (
          <li key={event.id}>
            <span>•</span>
            <div>
              <strong>{event.actorName}</strong>
              <p>
                {event.eventType} · sürüm {event.revisionNumber} ·{" "}
                {new Date(event.occurredAt).toLocaleString("tr-TR")}
              </p>
              <p>{event.reason ?? "Gerekçe yok"}</p>
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
}

function DecisionForm({
  form,
  busy,
  onDecide,
}: {
  form: ShiftProductionForm;
  busy: boolean;
  onDecide: (
    action: "APPROVE",
    explanation: string,
    missing: string,
  ) => Promise<void>;
}) {
  return (
    <form
      className="shift-decision-form"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        void onDecide(
          "APPROVE",
          String(data.get("explanation") ?? ""),
          String(data.get("missing") ?? ""),
        );
      }}
    >
      <label className="shift-form-field">
        <span>Karar açıklaması</span>
        <textarea name="explanation" />
      </label>
      {form.hasMissingMeasurements ? (
        <label className="shift-form-field">
          <span>Eksik ölçüm kabul gerekçesi *</span>
          <textarea name="missing" required />
        </label>
      ) : null}
      <div>
        <button className="shift-form-primary" disabled={busy} type="submit">
          Onayla
        </button>
      </div>
    </form>
  );
}
